<script setup>
import {ref, reactive, watch, toRef, computed} from 'vue'
import {useLxHelperFunctions, useLxValidationField, useThemeComposable} from 'lxvue3'
import * as yup from 'yup'
import {useI18n} from 'vue-i18n'

import {useCharacters} from '../../composables/characters.js'
import {useYupComposable} from '../../composables/yup.js'

const {t} = useI18n()
const {makeId} = useLxHelperFunctions()
const {setYup} = useYupComposable()
const {allowCharactersBackup, allowCharacters, checkField} = useCharacters()
const {primary} = useThemeComposable()

const emit = defineEmits(['update:modelValue', 'input'])

defineOptions({
  inheritAttrs: false
})

const props = defineProps({
  modelValue: [String, Number],
  schemaName: {
    type: [String],
    // required: true
  },
  formName: {
    type: [String],
    // required: true
  },
  showBackupCodes: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  name: [String]
})

const useBackupCode = ref(false)
const code = ref(props.modelValue)
const formName = 'twoFACodeInput'

const styles = reactive([
  {classes: 'blue-grey--text text--darken-2 grey lighten-3', text: 'blue-grey--text'},
  {
    classes: 'white--text',
    styles: 'background:rgba(255,255,255,0.25);border:1px solid white;border-radius:5px;',
    text: 'white--text'
  }
])

/***
 * wenn kein formularname übergeben wird und kein feldname werden dies automatisch
 * gesetzt als random id bzw. "UnvalidatedFieldWithoutForm"
 *
 * ACHTUNG: Für die Validierung soll immer schemaName und formName übergeben werden
 * **/
const schemaRef = props.schemaName ? toRef(props, 'schemaName') : ref(makeId(24))
const formRef = props.formName ? toRef(props, 'formName') : ref('UnvalidatedFieldWithoutForm')

// form validation

const codeLengthBackup = 8
const codeLengthAuth = 6

const compSchema = computed(() => {
  if (useBackupCode.value) {
    return yup.string()
      .required(t('field_validation.required'))
      .min(codeLengthBackup, t('field_validation.exact_length_characters', {count: codeLengthBackup}))
      .max(codeLengthBackup, t('field_validation.exact_length_characters', {count: codeLengthBackup}))
  } else {
    return yup.string()
      .required(t('field_validation.required'))
      .min(codeLengthAuth, t('field_validation.exact_length_characters', {count: codeLengthAuth}))
      .max(codeLengthAuth, t('field_validation.exact_length_characters', {count: codeLengthAuth}))
  }
})

setYup('passcodeSchema', compSchema)

const {
  value,
  valueChanged,
  setField
} = useLxValidationField(
  schemaRef.value,
  formRef.value
)

if (props.modelValue) {
  setField(props.modelValue, true)
}

// methods

/**
 * onInputChanged
 */
function inputChanged() {
  emit('update:modelValue', value.value ? value.value.toUpperCase() : null)
  emit('input', null)
  valueChanged()
}

/**
 * Clear value
 *
 * @param ev
 */
function clearValue(ev) {
  value.value = ''
  emit('resetAction', ev)
  inputChanged()
}

/**
 * Switch code state
 */
function switchCodeState() {
  value.value = ''
  inputChanged()
  setField('')
  useBackupCode.value = !useBackupCode.value
}

// watcher

watch(() => props.modelValue, (val) => {
  setField(val)
})

watch(useBackupCode, (val, oldVal) => {
  if (typeof val === 'boolean' && typeof oldVal === 'boolean' && val !== oldVal && code.value) {
    code.value = null
  }
})

// composable function call
emit('update:modelValue', value.value ? value.value.toUpperCase() : null)

</script>

<template>
  <div class="basis-full flex flex-row relative two-factor-field">

    <!-- HINT EMPTY VALUE -->

    <!--    <div class="absolute left-0 right-0 top-0 bottom-0 flex flex-col italic text-slate-500/50 font-light" style="pointer-events: none;z-index:1;" v-if="!value">-->
    <!--      <span class="mx-auto my-auto" style="transform: translateY(-11px)">{{ useBackupCode ? t('component_lx_two_fa_code_input.empty_hint_1') : t('component_lx_two_fa_code_input.empty_hint_2') }}</span>-->
    <!--    </div>-->

    <!-- INPUT FIELD -->

    <lx-input autofocus
              type="text"
              :schema-name="schemaRef"
              :form-name="formRef"
              :placeholder="useBackupCode ? t('component_lx_two_fa_code_input.empty_hint_1') : t('component_lx_two_fa_code_input.empty_hint_2')"
              v-model="value"
              :hide-details="true"
              :disabled="disabled"
              :data-cy-1="formName+'InputPasscode'"
              style="text-transform:uppercase"
              class="lx-2fa-input mx-3 !my-[15px] text-center bg-slate-400 !text-3xl basis-full"
              @keydown="checkField($event, useBackupCode ? allowCharactersBackup : allowCharacters)"
              @input="inputChanged"
              @blur="inputChanged"
              :icon="'mdi-barcode-scan'"
              :maxlength="useBackupCode ? codeLengthBackup : codeLengthAuth">
    </lx-input>
  </div>

  <!-- HINT -->

  <div class="text-left text-slate-500 mb-6 mt-4 leading-4 mx-2" v-if="showBackupCodes">
    <small :class="styles[0].text" v-if="!useBackupCode">
      {{ t('component_lx_two_fa_code_input.text_1') }}
      <a @click="switchCodeState" :class="['text-primary-600', styles[0].text]" :id="formName+'TwoFALink'" style="text-decoration: underline" class="cursor-pointer">
        <strong>{{ t('component_lx_two_fa_code_input.link') }}</strong>
      </a>
      {{ t('component_lx_two_fa_code_input.text_2') }}
    </small>

    <small :class="styles[0].text" v-if="useBackupCode">
      {{ t('component_lx_two_fa_code_input.text_3') }}
      <a @click="switchCodeState" :class="['text-primary-600', styles[0].text]" :id="formName+'TwoFALink'" style="text-decoration: underline" class="cursor-pointer">
        <strong>{{ t('component_lx_two_fa_code_input.link') }}</strong>
      </a>
      {{ t('component_lx_two_fa_code_input.text_4') }}
    </small>
  </div>
</template>
